import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { NavBar } from '../components/NavBar';
import { getDifficultyLabel } from '../utils/constants';
import { 
  Heart,
  HeartOff,
  Clock,
  Flame,
  Dumbbell,
  ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export const FavoritesPage = () => {
  const { api } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const res = await api().get('/favorites');
        setFavorites(res.data);
      } catch {
        // silent
      }
      setLoading(false);
    };
    fetchFavorites();
  }, [api]);

  const removeFavorite = async (workoutId) => {
    try {
      await api().delete(`/favorites/${workoutId}`);
      setFavorites(favorites.filter(w => w.id !== workoutId));
      toast.success('Removed from favorites');
    } catch (err) {
      toast.error('Failed to remove from favorites');
    }
  };

  const getDifficultyColor = (diff) => {
    switch(diff) {
      case 'beginner': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="favorites-page">
      <NavBar title="Favorites" />

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
        >
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-2">
            Your <span className="text-primary">Favorites</span>
          </h2>
          <p className="text-muted-foreground text-lg">Quick access to your saved workouts</p>
        </motion.div>

        {favorites.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((workout, i) => (
              <motion.div
                key={workout.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-card border border-border overflow-hidden group card-hover"
              >
                <div className="relative h-48">
                  <img 
                    src={workout.image_url}
                    alt={workout.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <button
                    onClick={() => removeFavorite(workout.id)}
                    className="absolute top-4 right-4 h-10 w-10 bg-black/50 backdrop-blur flex items-center justify-center hover:bg-black/70 transition-colors"
                    data-testid={`remove-favorite-${workout.id}`}
                  >
                    <HeartOff className="h-5 w-5 text-red-400" />
                  </button>
                  <div className="absolute bottom-4 left-4 flex gap-2">
                    <Badge className={`${getDifficultyColor(workout.difficulty)} border`}>
                      {getDifficultyLabel(workout.difficulty)}
                    </Badge>
                    <Badge className="bg-primary/20 text-primary border border-primary/30">
                      {workout.category}
                    </Badge>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-2">{workout.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{workout.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{workout.duration_minutes} min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Flame className="h-4 w-4" />
                      <span>{workout.calories_burned} cal</span>
                    </div>
                  </div>

                  <Link to={`/workout/${workout.id}`}>
                    <Button 
                      className="w-full bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90"
                    >
                      Start Workout
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Heart className="h-20 w-20 text-muted-foreground mx-auto mb-6" />
            <h3 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">No Favorites Yet</h3>
            <p className="text-muted-foreground text-lg mb-8">Start adding workouts to your favorites for quick access</p>
            <Link to="/workouts">
              <Button className="h-14 px-8 bg-primary text-primary-foreground font-heading tracking-wider uppercase">
                Browse Workouts
              </Button>
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};
